import React, { useEffect, useState } from "react";
import { mediaUploadManager, mediaOwnerKey } from "./createMediaUploadManager";
import { isMediaOptimizationEnabled, mediaApi } from "./mediaApi";
import { clearMediaReferences } from "./mediaReferences";
import { savedMediaCopiesMissing, savedMediaNotice, servableCopyCount } from "./mediaPresets";

type Row = { id: string; name: string; status: string; progress: number; message?: string };

/** A finished row ("Original saved and N copies made") leaves on its own after this long. */
export const ARCHIVED_ROW_LINGER_MS = 4000;

/**
 * SPA-level archival survives navigation; leaving the browser still interrupts network work.
 * The panel stays out of the way: one summary line while originals upload, it opens itself only when
 * something needs a hand (a paused upload, copies that never came), and finished rows clear themselves.
 * The archive and copy status also lives on each photo's media record, so nothing is lost when a row goes.
 */
export function OriginalArchivePanel() {
  const [collapsed, setCollapsed] = useState(true);
  const [rows, setRows] = useState<Record<string, Row>>({});
  useEffect(() => {
    if (!isMediaOptimizationEnabled()) return;
    let owner = "";
    let current = true;
    const syncOwner = () => {
      const next = mediaOwnerKey();
      if (owner === next) return;
      owner = next;
      mediaUploadManager.interruptAll();
      clearMediaReferences();
      setRows({});
      if (!next) return;
      void mediaUploadManager.restore().then(records => {
        if (!current || owner !== next) return;
        setRows(Object.fromEntries(records.map(job => [job.mediaId, {
          id: job.mediaId, name: job.fileName, status: job.status, progress: 0,
        }])));
        records.forEach(job => { void mediaUploadManager.resume(job.mediaId).catch(error => {
          if (current && owner === next) setRows(previous => ({ ...previous,
            [job.mediaId]: { id: job.mediaId, name: job.fileName, progress: 0,
              status: "ORIGINAL_FAILED_RETRYABLE", message: error.message },
          }));
        }); });
      }).catch(() => undefined);
    };
    const unsubscribe = mediaUploadManager.subscribe((id, status, progress, message) => {
      if (!id || !current || status === "MASTER_UPLOADING" || status === "OPTIMIZING_MASTER") return;
      setRows(previous => ({ ...previous, [id]: {
        id, name: previous[id]?.name || "Photo", status, progress,
        message: status === "ORIGINAL_ARCHIVED" ? (message || savedMediaNotice(0)) : message,
      } }));
    });
    syncOwner();
    // Same-tab localStorage logout/switch does not emit a storage event.
    const timer = window.setInterval(syncOwner, 1000);
    window.addEventListener("storage", syncOwner);
    return () => { current = false; window.clearInterval(timer); window.removeEventListener("storage", syncOwner); unsubscribe(); mediaUploadManager.interruptAll(); };
  }, []);
  const list = Object.values(rows);
  const archiving = list.filter(row => row.status !== "ORIGINAL_ARCHIVED");
  const waitingKey = list.filter(row => row.status === "ORIGINAL_ARCHIVED" && row.message === savedMediaNotice(0))
    .map(row => row.id).sort().join("\n");
  useEffect(() => {
    const ids = waitingKey ? waitingKey.split("\n") : [];
    if (!ids.length) return undefined;
    let stopped = false;
    const tick = () => {
      ids.forEach(id => {
        void mediaApi.get(id).then(view => {
          if (stopped) return;
          const copies = servableCopyCount(view);
          if (view.archiveStatus !== "ORIGINAL_ARCHIVED" || copies < 1) return;
          setRows(previous => {
            const current = previous[id];
            if (!current || current.message !== savedMediaNotice(0)) return previous;
            return { ...previous, [id]: { ...current, message: savedMediaNotice(copies) } };
          });
        }).catch(() => undefined);
      });
    };
    tick();
    const interval = window.setInterval(tick, 3000);
    const giveUp = window.setTimeout(() => {
      window.clearInterval(interval);
      if (stopped) return;
      setRows(previous => {
        const next = { ...previous };
        ids.forEach(id => {
          const current = next[id];
          if (current?.message === savedMediaNotice(0)) next[id] = { ...current, message: savedMediaCopiesMissing };
        });
        return next;
      });
    }, 45000);
    return () => { stopped = true; window.clearInterval(interval); window.clearTimeout(giveUp); };
  }, [waitingKey]);
  // Finished rows go on their own; a row still waiting for copies, or missing them, stays until it is resolved or dismissed.
  const doneKey = list.filter(row => row.status === "ORIGINAL_ARCHIVED"
    && row.message !== savedMediaNotice(0) && row.message !== savedMediaCopiesMissing)
    .map(row => row.id).sort().join("\n");
  useEffect(() => {
    const ids = doneKey ? doneKey.split("\n") : [];
    if (!ids.length) return undefined;
    const timer = window.setTimeout(() => setRows(previous => {
      const next = { ...previous };
      ids.forEach(id => { delete next[id]; });
      return next;
    }), ARCHIVED_ROW_LINGER_MS);
    return () => window.clearTimeout(timer);
  }, [doneKey]);
  // Open by itself when something needs a hand: a paused upload (Retry / Reselect) or copies that never came.
  const attentionKey = list.filter(row => row.status === "ORIGINAL_FAILED_RETRYABLE" || row.message === savedMediaCopiesMissing)
    .map(row => row.id).sort().join("\n");
  useEffect(() => {
    if (attentionKey) setCollapsed(false);
  }, [attentionKey]);
  useEffect(() => {
    if (!archiving.length) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [archiving.length]);
  if (!list.length) return null;
  const summary = archiving.length
    ? `Original photos (${archiving.length})`
    : list.length === 1 ? (list[0].message || "Original saved").replace(/\.$/, "") : `Original saved (${list.length})`;
  return <aside aria-label="Original photo uploads" className="hkp-original-archives">
    <button type="button" className="btn btn-sm p-0" aria-expanded={!collapsed}
      onClick={() => setCollapsed(value => !value)}>
      {summary} — {collapsed ? "Show" : "Minimize"}
    </button>
    {!collapsed && <>
    {archiving.length > 0 && <p className="mb-1">You can save your product. Keep HKP open while originals upload.</p>}
    {list.map(row => <div key={row.id} className="mb-2">
      {row.status === "ORIGINAL_ARCHIVED" ? <>
        <span>{row.name}: {row.message}</span>
        <button type="button" className="btn btn-sm btn-link" onClick={() => setRows(previous => {
          const next = { ...previous }; delete next[row.id]; return next;
        })}>Dismiss</button>
      </> : <>
      <span>{row.name}: {row.status === "ORIGINAL_FAILED_RETRYABLE" ? "Upload paused" : `${row.progress}%`}</span>
      {row.message && <small className="d-block">{row.message}</small>}
      {row.status === "ORIGINAL_FAILED_RETRYABLE" && <>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => {
          void mediaUploadManager.resume(row.id).catch(() => undefined);
        }}>Retry</button>
        <label className="btn btn-sm btn-outline-secondary ml-2">Reselect original
          <input type="file" accept="image/*" className="sr-only" onChange={event => {
            const file = event.target.files?.[0];
            if (file) void mediaUploadManager.resume(row.id, file).catch(error => setRows(previous => ({
              ...previous, [row.id]: { ...row, message: error.message },
            })));
            event.target.value = "";
          }} />
        </label>
      </>}
      </>}
    </div>)}
    </>}
  </aside>;
}
